import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { RevealText } from "./Reveal";
import { IMAGES } from "../../data/content";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex flex-col justify-end"
    >
      {/* Parallax background */}
      <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0 -z-10">
        <img
          src={IMAGES.heroAbstract}
          alt=""
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-background" />
        <div className="absolute inset-0 grid-lines opacity-60" />
      </motion.div>

      <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-10 pb-16 pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="font-mono-ui text-[11px] font-bold uppercase tracking-[0.25em] text-primary">
            Official FireFlink Channel Partner · United Kingdom
          </span>
        </motion.div>

        <h1 className="font-heading font-medium tracking-tighter leading-[0.9] text-[13vw] sm:text-[10vw] lg:text-[8.5rem] text-balance">
          <RevealText
            delay={0.25}
            lines={[
              <>Ship software</>,
              <>
                <span className="text-muted-foreground">that never</span>
              </>,
              <>
                breaks<span className="text-primary">.</span>
              </>,
            ]}
          />
        </h1>

        <div className="mt-10 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="max-w-xl text-base lg:text-lg leading-relaxed text-muted-foreground"
          >
            Interlocked Solutions brings{" "}
            <span className="text-foreground">FireFlink's AI-powered, scriptless test
            automation</span>{" "}
            to UK teams — with local licensing, hands-on onboarding and managed support.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="flex items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-transform duration-300 hover:scale-[1.04]"
            >
              Book a demo
              <ArrowUpRight
                size={17}
                strokeWidth={2}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
            <a
              href="#platform"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 text-sm text-foreground transition-colors duration-300 hover:border-foreground"
            >
              Explore the platform
            </a>
          </motion.div>
        </div>

        <motion.div
          style={{ opacity }}
          className="mt-16 flex items-center gap-2 text-muted-foreground"
        >
          <ArrowDown size={16} strokeWidth={1.5} className="animate-bounce" />
          <span className="font-mono-ui text-[11px] uppercase tracking-[0.2em]">Scroll</span>
        </motion.div>
      </div>
    </section>
  );
};
